import { RumEvent } from "@datadog/browser-rum";

/**
 * Error message patterns that are dropped before being sent to Datadog RUM.
 *
 * These are known third-party / environmental noise that we cannot fix in our
 * code and that otherwise drown out real bugs in dashboards and SLOs. Keep this
 * list small and well-justified — every entry should come with a comment
 * explaining the source and why it is safe to drop.
 *
 * When adding a new pattern:
 *   - Confirm it shows up as a high-volume, non-actionable fingerprint in RUM
 *     (see the frontend-error-triage skill).
 *   - Prefer the most specific regex that still matches the fingerprint (so we
 *     don't accidentally hide legitimate errors with similar wording).
 *   - Leave a comment with the source (browser / extension / proxy) and a link
 *     to the triage or ticket that justifies the entry.
 */
export const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Microsoft Outlook "Safe Links" URL scanner. The crawler evaluates page
  // scripts out-of-context and throws this from its own injected code. Not our
  // bug, not reproducible in a real browser session.
  /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4/,
  // Cross-origin script threw and the browser stripped the details due to CORS.
  // Without a `crossorigin` attribute on the 3rd-party script tag we cannot
  // recover any useful info, so the event is pure noise.
  /^Script error\.?$/,
  // Zscaler proxy (corporate network) being blocked by our own CSP frame-src.
  // The CSP is working as intended; the user's network is injecting the iframe.
  /^csp_violation: 'https:\/\/gateway\.zscaler\.net'/,
];

/**
 * Predicate used by the RUM `beforeSend` hook. Returns `false` when the event
 * should be dropped (matches an ignored pattern) and `true` otherwise.
 *
 * Exported so it can be unit-tested and so future filters can be composed
 * without reaching into `datadogRum.init`.
 */
export function shouldDropRumEvent(event: RumEvent): boolean {
  if (event.type !== "error") {
    return false;
  }

  const message = event.error?.message ?? "";

  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
